import { z } from "zod";

import { readWithIdleTimeout, STREAM_IDLE_TIMEOUT_MS } from "@common/utils/stream-idle-read";

const repairMergeStreamLine = z.discriminatedUnion("type", [
  z.object({ type: z.literal("keepalive") }),
  z.object({
    type: z.literal("result"),
    repaired_ooxml: z.string(),
    repair_summary: z.string(),
  }),
  z.object({ type: z.literal("error"), error: z.string() }),
]);

export type RepairMergeStreamLine = z.infer<typeof repairMergeStreamLine>;
export type RepairMergeStreamResult = Extract<RepairMergeStreamLine, { type: "result" }>;

export async function drainRepairMergeStream(
  stream: ReadableStream<Uint8Array>,
): Promise<RepairMergeStreamResult> {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const read = await readWithIdleTimeout(reader, STREAM_IDLE_TIMEOUT_MS);
      if (read === "idle") {
        throw new Error("The repair stream went idle before returning a result.");
      }

      let lines: string[];
      if (read.done) {
        lines = [buffer];
      } else {
        buffer += decoder.decode(read.value, { stream: true });
        const segments = buffer.split("\n");
        buffer = segments.pop() ?? "";
        lines = segments;
      }

      for (const line of lines) {
        const event = parseStreamLine(line);
        if (event?.type === "error") {
          throw new Error(event.error);
        }
        if (event?.type === "result") {
          return event;
        }
      }

      if (read.done) {
        throw new Error("The repair stream ended without a result.");
      }
    }
  } finally {
    reader.cancel().catch(() => undefined);
  }
}

function parseStreamLine(line: string): RepairMergeStreamLine | null {
  const trimmed = line.trim();
  if (!trimmed) return null;
  const parsed = repairMergeStreamLine.safeParse(jsonOrNull(trimmed));
  return parsed.success ? parsed.data : null;
}

function jsonOrNull(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
